import { useState } from "react";
import { Upload, message } from "antd";
import UploadIcon from "@/components/icons/Upload";
import { hackathonApi } from "@/services/hackathon";
import type { UploadRequestOption } from "rc-upload/lib/interface";
import Spinner from "./Spinner";

interface ImageGalleryUploaderProps {
  actionType: "add" | "edit";
  imageGallery: { id: string; value: string }[];
  setImageGallery: React.Dispatch<
    React.SetStateAction<{ id: string; value: string }[]>
  >;
  setIsUploadingGallery: React.Dispatch<React.SetStateAction<boolean>>;
  single?: boolean;
  className?: string;
}

const ImageGalleryUploader = ({
  imageGallery,
  setImageGallery,
  setIsUploadingGallery,
  className,
}: ImageGalleryUploaderProps) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleUploadGalleryImages = async (file: File) => {
    try {
      setIsUploadingGallery(true);
      setIsLoading(true);

      const response = await hackathonApi.uploadFile({
        image: file,
        folder: 'hackathons'
      });

      if (response.success) {
        const newImageGallery = [
          {
            id: Date.now().toString(),
            value: response.data.url,
          },
        ];
        setImageGallery(newImageGallery);
        message.success('Image uploaded successfully!');
      } else {
        message.error('Upload failed');
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Upload failed');
    } finally {
      setIsLoading(false);
      setIsUploadingGallery(false);
    }
  };

  const handleDelete = () => {
    setImageGallery([]);
  };



  const customRequest = async ({ file, onSuccess, onError }: UploadRequestOption) => {
    try {
      await handleUploadGalleryImages(file as File);
      if (onSuccess) onSuccess("ok");
    } catch (error) {
      if (onError) onError(error as Error | ProgressEvent<EventTarget>);
    }
  };

  return (
    <div className="w-full">
      <Upload.Dragger
        name="file"
        accept=".png,.jpg,.jpeg,.svg"
        showUploadList={false}
        customRequest={customRequest}
        className={`w-full h ${className}`}
        disabled={isLoading} // optionally disable during upload
      >
        <div className="flex justify-center items-center min-h-full">
          {isLoading ? (
         
            <Spinner color="#560591" height={20} width={20} ariaLabel="loading" />
          ) : imageGallery.length > 0 ? (
            <div className="relative w-[100px] h-[98px] flex justify-center items-center">
              <img
                src={imageGallery[0].value}
                alt="Uploaded Image"
                width={20}
                height={20}
                className="rounded-2xl object-cover w-full h-full"
              />
              <button
                type="button"
                className="absolute -top-2 -right-2 w-7 h-7 bg-white rounded-full shadow-md text-gray-700"
                onClick={handleDelete}
              >
                ×
              </button>
            </div>
          ) : (
            <div className="text-center">
              <p className="text-2xl text-gray-400 mb-2 flex items-center justify-center">
                <UploadIcon />
              </p>
              <p className="ant-upload-text !text-gray-600">
                <span className="text-sm font-semibold text-[#6941C6]">
                  Click to upload
                </span>{" "}
                or drag and drop
              </p>
              <p className="text-xs text-gray-400">SVG, PNG, JPG (Max size: 10MB)</p>
            </div>
          )}
        </div>
      </Upload.Dragger>
    </div>
  );
};

export default ImageGalleryUploader;
